import {
  AbarbeitungStatusType,
  VorgangProzessSchrittStatus,
} from '@core/domain';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useDeleteVorgang,
  useRestoreVorgang,
  useRetryVorgang,
  useSaveVorgang,
  useZurueckstellenVorgang,
} from '@/adapter/opm-cockpit-backend';
import { useAuth } from '@/features/auth/AuthProvider';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { useSchemaValidation } from '@/features/schemaValidation/useSchemaValidation';
import { VorgangHistorie } from '@/features/vorgangHistorie';
import { getPreviousPayloadForSchritt } from '@/features/vorgangHistorie/utils/getPreviousPayloadForSchritt';
import { useVorgangLockContext } from '@/features/vorgangLocking/VorgangLockProvider';
import { VorgangDetail, VorgangDetailProtokoll } from '@/types';
import {
  ArchivedVorgangActions,
  ErrorMessage,
  LockWarningAlert,
  VorgangActions,
  VorgangDataEditor,
  VorgangHeader,
  VorgangInformation,
} from './components';
import { getLetztenSchrittDerAbarbeitung } from './getLetztenSchrittDerAbarbeitung';
import { useArchivedActionGuard } from './hooks/useArchivedActionGuard';

interface VorgangBearbeitungContentProps {
  vorgang: VorgangDetail;
  protokoll: VorgangDetailProtokoll;
  isArchived: boolean;
  backPath: string;
  error: string | null;
  setError: (error: string | null) => void;
  navigate: ReturnType<typeof useNavigate>;
}

export const VorgangBearbeitungContent: React.FC<
  VorgangBearbeitungContentProps
> = ({
  vorgang,
  error,
  protokoll,
  isArchived,
  backPath,
  setError,
  navigate,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const { abarbeitungId, schrittId } = useParams<{
    abarbeitungId?: string;
    schrittId?: string;
  }>();
  const currentUser = user?.email || '';
  const userEmail = user?.email;
  const retryMutation = useRetryVorgang();
  const saveMutation = useSaveVorgang();
  const deleteMutation = useDeleteVorgang();
  const restoreMutation = useRestoreVorgang();
  const zurueckstellenMutation = useZurueckstellenVorgang();

  // Get lock state from context
  const { isLockedByCurrentUser, isLocking, isUnlocking, lockVorgang } =
    useVorgangLockContext();
  const { guard, ArchiveConfirmDialog } = useArchivedActionGuard(isArchived);

  const [rawEventData, setRawEventData] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const letzteAbarbeitung = vorgang?.abarbeitungen.find(
    (a) => vorgang.letzteAbarbeitungId === a.id,
  );

  const getAusgewaehlterSchrittOderLetzter = () => {
    if (schrittId) {
      return vorgang.abarbeitungen
        .find((a) => a.id === abarbeitungId)
        ?.schritte.find((s) => s.id === schrittId);
    }

    return getLetztenSchrittDerAbarbeitung(letzteAbarbeitung);
  };

  const ausgewaehlterSchritt = useMemo(getAusgewaehlterSchrittOderLetzter, [
    letzteAbarbeitung,
    vorgang.abarbeitungen,
    schrittId,
  ]);

  const istAusgewaehlteAbarbeitungAktuelleAbarbeitung =
    !ausgewaehlterSchritt || abarbeitungId === letzteAbarbeitung?.id;
  const isArchivierungsschritt =
    (ausgewaehlterSchritt?.schritt as VorgangProzessSchritt | undefined) ===
    VorgangProzessSchritt.ARCHIVIERUNG;
  const showRetry =
    !isArchivierungsschritt ||
    (letzteAbarbeitung?.status === AbarbeitungStatusType.FEHLER &&
      istAusgewaehlteAbarbeitungAktuelleAbarbeitung &&
      ausgewaehlterSchritt?.status === VorgangProzessSchrittStatus.FEHLER);

  const isDeleted = vorgang.deletedAt != null;

  // Use the schema validation hook - only step validation
  const { validationErrors, isValid, validateJson, getSchemaDefinition } =
    useSchemaValidation({
      stepType: ausgewaehlterSchritt?.schritt as VorgangProzessSchritt,
    });
  const schema = getSchemaDefinition();

  // Derived loading states from mutations
  const actionLoading = isLocking
    ? 'lock'
    : isUnlocking
      ? 'unlock'
      : retryMutation.isPending
        ? 'retry'
        : saveMutation.isPending
          ? 'save'
          : deleteMutation.isPending
            ? 'delete'
            : restoreMutation.isPending
              ? 'restore'
              : zurueckstellenMutation.isPending
                ? 'zurueckstellen'
                : null;

  // Effect to initialize data when item is loaded (only once)
  useEffect(() => {
    if (vorgang && ausgewaehlterSchritt) {
      // Use target step's initialPayload if available, otherwise use the first step of the latest abarbeitung
      const payloadToUse = ausgewaehlterSchritt.initialPayload;

      const eventDataString = JSON.stringify(payloadToUse, null, 2);
      setRawEventData(eventDataString);

      validateJson(eventDataString);
      // Note: Locking is now handled automatically by VorgangLockProvider
    }
  }, [vorgang?.id, ausgewaehlterSchritt?.id, validateJson]); // Include ausgewaehlterSchritt.id to reinitialize when step changes

  /**
   * If no schritt is selected or provided in the URL,
   * when first loading the page, we navigate to the first step of the latest abarbeitung.
   */
  useEffect(() => {
    const letzterSchritt = getLetztenSchrittDerAbarbeitung(letzteAbarbeitung);
    if (!abarbeitungId && !schrittId && letzteAbarbeitung && letzterSchritt) {
      navigate(
        `/vorgaenge/${vorgang.id}/abarbeitungen/${letzteAbarbeitung.id}/schritte/${letzterSchritt.id}`,
        {
          replace: true,
          // Herkunft (location.state.from) über den internen Redirect hinweg
          // bewahren, damit der Rücksprung zur Quellseite erhalten bleibt.
          state: location.state,
        },
      );
    }
  }, [
    abarbeitungId,
    schrittId,
    navigate,
    vorgang.id,
    letzteAbarbeitung?.id,
    letzteAbarbeitung?.schritte,
    location.state,
  ]);

  const handleEventDataChange = (value: string) => {
    setRawEventData(value);

    // Validate using the hook (debouncing is handled internally)
    validateJson(value);

    setHasUnsavedChanges(
      value !== JSON.stringify(ausgewaehlterSchritt?.initialPayload, null, 2),
    );
  };

  const handleRetry = async () => {
    if ((!isArchived && !isValid) || !vorgang || !userEmail || !showRetry)
      return;

    try {
      JSON.parse(rawEventData);
    } catch {
      setError('Die Event-Daten müssen gültiges JSON sein.');
      return;
    }

    retryMutation.mutate(
      {
        id: vorgang.id,
        eventData: rawEventData,
        initiator: userEmail,
        startSchritt: ausgewaehlterSchritt?.schritt,
      },
      {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          if (isArchived) {
            lockVorgang();
          }
          toast.success(
            'Vorgang wurde erfolgreich zur erneuten Verarbeitung gesendet.',
          );
        },
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim erneuten Verarbeiten des Vorgangs',
          );
        },
      },
    );
  };

  const handleDelete = async () => {
    if (!vorgang) {
      return;
    }

    deleteMutation.mutate(
      { id: vorgang.id },
      {
        onSuccess: () => {
          navigate(backPath);
        },
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Löschen des Vorgangs',
          );
        },
      },
    );
  };

  const handleRestore = async () => {
    if (!vorgang) {
      return;
    }

    restoreMutation.mutate(
      { id: vorgang.id },
      {
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Wiederherstellen des Vorgangs',
          );
        },
      },
    );
  };

  const handleZurueckstellen = (kommentar: string) => {
    if (!vorgang || !userEmail) {
      return;
    }

    zurueckstellenMutation.mutate(
      {
        id: vorgang.id,
        initiator: userEmail,
        kommentar,
      },
      {
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Zurückstellen des Vorgangs',
          );
        },
      },
    );
  };

  const handleSave = async () => {
    if (!isValid || !vorgang || !userEmail) return;

    saveMutation.mutate(
      {
        id: vorgang.id,
        eventData: rawEventData,
        initiator: userEmail,
      },
      {
        onSuccess: () => {
          setHasUnsavedChanges(false);
        },
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Speichern der Änderungen',
          );
        },
      },
    );
  };

  const hasRunningAbarbeitung =
    letzteAbarbeitung?.status === AbarbeitungStatusType.LAUFEND;

  const waitingStep = letzteAbarbeitung?.schritte.find(
    (s) => s.schritt === letzteAbarbeitung.aktuellerSchritt && !s.finishedAt,
  );
  const retryDisabledReason =
    letzteAbarbeitung?.status === AbarbeitungStatusType.WARTEND &&
    !(
      waitingStep &&
      Date.now() - new Date(waitingStep.createdAt).getTime() >= 60 * 60 * 1000
    )
      ? 'Wartende Vorgänge können nach einer Stunde erneut verarbeitet werden.'
      : null;

  const previousPayload = getPreviousPayloadForSchritt({
    schritt: ausgewaehlterSchritt,
    abarbeitungId,
    abarbeitungen: vorgang.abarbeitungen,
    protokoll,
  });

  const canPreview = useMemo(() => {
    if (!ausgewaehlterSchritt || isDeleted) {
      return false;
    }

    const step = ausgewaehlterSchritt.schritt as VorgangProzessSchritt;

    return [
      VorgangProzessSchritt.FACHLICHE_VALIDIERUNG,
      VorgangProzessSchritt.WEITERLEITUNG_AN_DOKUMENTDIENSTLEISTER,
    ].includes(step);
  }, [ausgewaehlterSchritt, isDeleted]);

  const deleteDisabledReason = useMemo(() => {
    if (letzteAbarbeitung.status === AbarbeitungStatusType.LAUFEND) {
      return 'Laufende Vorgänge können nicht gelöscht werden.';
    }

    if (letzteAbarbeitung.status === AbarbeitungStatusType.ERFOLGREICH) {
      return 'Erfolgreich abgeschlossene Vorgänge können nicht gelöscht werden.';
    }

    return null;
  }, [letzteAbarbeitung.status]);

  return (
    <div className="space-y-6">
      <VorgangHeader
        vorgang={vorgang}
        hasUnsavedChanges={hasUnsavedChanges}
        isArchived={isArchived}
        onBack={() => navigate(backPath)}
      />

      {!isArchived && (
        <LockWarningAlert vorgang={vorgang} currentUser={currentUser} />
      )}

      <VorgangInformation vorgang={vorgang} />

      <ErrorMessage errorMessage={error} />

      <div className="grid grid-cols-1 xl:grid-cols-[30%_70%]">
        <div className="space-y-6">
          <VorgangHistorie vorgang={vorgang} protokoll={protokoll} />
        </div>

        <div className="pl-6 space-y-6">
          <VorgangDataEditor
            rawEventData={rawEventData}
            ausgewaehlterSchritt={ausgewaehlterSchritt}
            previousPayload={
              previousPayload
                ? JSON.stringify(previousPayload, null, 2)
                : undefined
            }
            validationErrors={validationErrors}
            isValid={isValid}
            isLockedByCurrentUser={
              isLockedByCurrentUser && !isDeleted && !isArchivierungsschritt
            }
            canEditWithoutLock={isArchived && !isArchivierungsschritt}
            isDeleted={isDeleted}
            onEventDataChange={handleEventDataChange}
            validateJson={validateJson}
            schema={schema?.jsonSchema}
            canPreview={canPreview}
            vorgangId={vorgang.id}
          />
          {isArchived ? (
            <ArchivedVorgangActions
              istAktuelleAbarbeitung={
                istAusgewaehlteAbarbeitungAktuelleAbarbeitung
              }
              actionLoading={actionLoading}
              onRetry={() => guard(handleRetry)}
              showRetry={showRetry}
              archiveRetry={isArchivierungsschritt}
              isUserAuthenticated={!!userEmail}
            />
          ) : (
            <VorgangActions
              isValid={isValid}
              retryDisabledReason={retryDisabledReason}
              deleteDisabledReason={deleteDisabledReason}
              hasRunningAbarbeitung={hasRunningAbarbeitung}
              canSave={hasUnsavedChanges && !isArchivierungsschritt}
              istAktuelleAbarbeitung={
                istAusgewaehlteAbarbeitungAktuelleAbarbeitung
              }
              canZurueckstellen={
                letzteAbarbeitung.status !==
                AbarbeitungStatusType.ZURUECKGESTELLT
              }
              isDeleted={isDeleted}
              actionLoading={actionLoading}
              onRetry={() => guard(handleRetry)}
              showRetry={showRetry}
              archiveRetry={isArchivierungsschritt}
              onSave={() => guard(handleSave)}
              onDelete={() => guard(handleDelete)}
              onRestore={() => guard(handleRestore)}
              onZurueckstellen={(kommentar) =>
                guard(() => handleZurueckstellen(kommentar))
              }
              isUserAuthenticated={!!userEmail}
            />
          )}
        </div>
      </div>
      {ArchiveConfirmDialog}
    </div>
  );
};
